import { useState } from 'react'
import { Sparkles, Copy } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function ColorSchemes() {
  const [baseColor, setBaseColor] = useState('#3b82f6')
  const [schemeType, setSchemeType] = useState('complementary')
  const [scheme, setScheme] = useState([])

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null
  }

  const rgbToHsl = (r, g, b) => {
    r /= 255
    g /= 255
    b /= 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h, s, l = (max + min) / 2

    if (max === min) {
      h = s = 0
    } else {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
        case g: h = ((b - r) / d + 2) / 6; break
        case b: h = ((r - g) / d + 4) / 6; break
      }
    }
    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)]
  }

  const hslToRgb = (h, s, l) => {
    h /= 360
    s /= 100
    l /= 100
    let r, g, b

    if (s === 0) {
      r = g = b = l
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1
        if (t > 1) t -= 1
        if (t < 1/6) return p + (q - p) * 6 * t
        if (t < 1/2) return q
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
        return p
      }
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s
      const p = 2 * l - q
      r = hue2rgb(p, q, h + 1/3)
      g = hue2rgb(p, q, h)
      b = hue2rgb(p, q, h - 1/3)
    }
    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)]
  }

  const rgbToHex = (r, g, b) => {
    return '#' + [r, g, b].map(x => {
      const hex = x.toString(16)
      return hex.length === 1 ? '0' + hex : hex
    }).join('')
  }

  const generateScheme = () => {
    const rgb = hexToRgb(baseColor)
    if (!rgb) return

    const [h, s, l] = rgbToHsl(rgb.r, rgb.g, rgb.b)
    const colors = [baseColor]

    switch (schemeType) {
      case 'complementary':
        const compH = (h + 180) % 360
        const compRgb = hslToRgb(compH, s, l)
        colors.push(rgbToHex(compRgb[0], compRgb[1], compRgb[2]))
        break

      case 'triadic':
        const tri1 = (h + 120) % 360
        const tri2 = (h + 240) % 360
        const tri1Rgb = hslToRgb(tri1, s, l)
        const tri2Rgb = hslToRgb(tri2, s, l)
        colors.push(rgbToHex(tri1Rgb[0], tri1Rgb[1], tri1Rgb[2]))
        colors.push(rgbToHex(tri2Rgb[0], tri2Rgb[1], tri2Rgb[2]))
        break

      case 'analogous':
        const ana1 = (h + 30) % 360
        const ana2 = (h - 30 + 360) % 360
        const ana1Rgb = hslToRgb(ana1, s, l)
        const ana2Rgb = hslToRgb(ana2, s, l)
        colors.push(rgbToHex(ana1Rgb[0], ana1Rgb[1], ana1Rgb[2]))
        colors.push(rgbToHex(ana2Rgb[0], ana2Rgb[1], ana2Rgb[2]))
        break

      case 'monochromatic':
        colors.push(rgbToHex(...hslToRgb(h, s, Math.max(0, l - 20))))
        colors.push(rgbToHex(...hslToRgb(h, s, Math.min(100, l + 20))))
        break

      case 'split-complementary':
        const split1 = (h + 150) % 360
        const split2 = (h + 210) % 360
        const split1Rgb = hslToRgb(split1, s, l)
        const split2Rgb = hslToRgb(split2, s, l)
        colors.push(rgbToHex(split1Rgb[0], split1Rgb[1], split1Rgb[2]))
        colors.push(rgbToHex(split2Rgb[0], split2Rgb[1], split2Rgb[2]))
        break

      case 'tetradic':
        const tet1 = (h + 90) % 360
        const tet2 = (h + 180) % 360
        const tet3 = (h + 270) % 360
        const tet1Rgb = hslToRgb(tet1, s, l)
        const tet2Rgb = hslToRgb(tet2, s, l)
        const tet3Rgb = hslToRgb(tet3, s, l)
        colors.push(rgbToHex(tet1Rgb[0], tet1Rgb[1], tet1Rgb[2]))
        colors.push(rgbToHex(tet2Rgb[0], tet2Rgb[1], tet2Rgb[2]))
        colors.push(rgbToHex(tet3Rgb[0], tet3Rgb[1], tet3Rgb[2]))
        break
    }

    setScheme(colors)
  }

  const copyScheme = () => {
    navigator.clipboard.writeText(scheme.join(', '))
    alert('Color scheme copied to clipboard!')
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-3">Base Color:</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={baseColor}
                  onChange={(e) => setBaseColor(e.target.value)}
                  className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={baseColor}
                  onChange={(e) => setBaseColor(e.target.value)}
                  className="input flex-1"
                  placeholder="#3b82f6"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-3">Scheme Type:</label>
              <select
                value={schemeType}
                onChange={(e) => setSchemeType(e.target.value)}
                className="input w-full"
              >
                <option value="complementary">Complementary</option>
                <option value="triadic">Triadic</option>
                <option value="analogous">Analogous</option>
                <option value="monochromatic">Monochromatic</option>
                <option value="split-complementary">Split Complementary</option>
                <option value="tetradic">Tetradic</option>
              </select>
            </div>
          </div>

          <Button onClick={generateScheme} variant="primary" className="w-full">
            <Sparkles className="w-5 h-5 mr-2" />
            Generate Color Scheme
          </Button>
        </Card.Body>
      </Card>

      {scheme.length > 0 && (
        <Card>
          <Card.Body>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Generated Scheme:</h3>
              <Button onClick={copyScheme} variant="outline" size="sm">
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {scheme.map((color, index) => (
                <div key={index} className="space-y-2">
                  <div
                    className="w-full h-24 rounded-lg border-2 border-gray-200"
                    style={{ backgroundColor: color }}
                  />
                  <div className="text-center">
                    <p className="text-sm font-mono text-gray-700">{color}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {scheme.length === 0 && (
        <Card>
          <Card.Body className="text-center py-12">
            <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Select a base color and scheme type, then click "Generate Color Scheme"</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

